import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CallerService } from '../services/caller.service';
import { AppState, CorpMemberState, PpaModel } from '../ngrx-store/app.state';
import { Store } from '@ngrx/store';
import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { MoreViewSearchComponent } from '../dialogs/more-view-search/more-view-search.component';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { combineLatest, ReplaySubject, Subject } from 'rxjs';
import { selectFeatureCorpMember, selectFeaturePpas } from '../ngrx-store/selectors/corp.selectors';
import { morePpasLoaded, ppasLoaded } from '../ngrx-store/actions/corp-member.actions';
import { titleCase } from '../utils/sale.util';
import { PPA_PHONE_QUERY, PpaView, ppaListClass, readPpaView, savePpaView } from '../utils/ppa.util';

type PpaSort = 'top_rated' | 'most_reviewed' | 'most_corpers' | 'newest'

@Component({
  selector: 'app-ppa-page',
  templateUrl: './ppa-page.component.html',
  styleUrls: ['./ppa-page.component.css']
})
export class PpaPageComponent implements OnInit, OnDestroy {

  constructor(
    public callerService: CallerService,
    public store: Store<AppState>,
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
) {}

  // Filter form
  // TODO: a "Search PPAs in Ikeja" box above the list, once a state or LGA has more PPAs than people
  // will scroll through - say over 50 (about 4 pages / "Show more"s); check the biggest with
  // SELECT state_lga_id, COUNT(DISTINCT ppa_id) FROM Locations GROUP BY 1 ORDER BY 2 DESC. Someone
  // looking for their own PPA wants to type its name, and the search bar at the top searches
  // everything (houses, items). Show it only past that number (totalPpas), so small places stay a
  // plain list. GET /ppas has no name filter yet: add `q` there (matched like utilities/ppaNames.js,
  // with the state/LGA filters and paging it already has) rather than loading the whole place to
  // match in the browser - this list is paged. Debounce it (~300ms, from 2-3 letters), clear it with
  // the filters ("Back to my state"), and say "No PPAs like 'x' in Ikeja - add it" with the Add button.
  filterForm: FormGroup = new FormGroup({
    state_id: new FormControl(''),
    state_lga_id: new FormControl(''),
  })

  // States and LGAs
  ng_states: any[] = []
  ng_states_lgas: any[] = []

  // Pagination. On a phone, `currentPage` is how many pages are shown (each "Show more" adds one).
  currentPage: number = 1
  pageSize: number = 12

  /**
   * A phone: PPAs as compact rows (several to a screen, not ~1.5 tall cards), and "Show more" adds
   * the next page below instead of page numbers.
   */
  isPhone = false

  /** The corp member's service state - the page opens filtered to it. Null if unknown. */
  defaultStateId: number | null = null

  readonly sortOptions: { value: PpaSort; label: string }[] = [
    { value: 'top_rated', label: 'Top rated' },
    { value: 'most_reviewed', label: 'Most reviewed' },
    { value: 'most_corpers', label: 'Most corpers' },
    { value: 'newest', label: 'Newest' },
  ]
  sortBy: PpaSort = 'top_rated'

  /** 'list' rows compare better on wider screens; remembered per browser. */
  view: PpaView = readPpaView()

  readonly ppaListClass = ppaListClass

  // Replays: a cached states response (CacheInterceptor) arrives synchronously, before the
  // combineLatest below subscribes - a plain Subject would drop it and the page would never load.
  private statesLoaded$ = new ReplaySubject<void>(1)

  /** The current page, from the server via the store (see loadPpas). */
  displayPpaData: PpaModel[] = []

  /** Matching PPAs across all pages. */
  totalPpas: number = 0

  private destroy$ = new Subject<void>()

  get currentSortLabel(): string {
    return this.sortOptions.find((option) => option.value === this.sortBy)?.label ?? ''
  }

  setSort(sort: PpaSort): void {
    this.sortBy = sort
    this.currentPage = 1
    this.loadPpas()
  }

  /** Keeps a card when its PPA is replaced (e.g. new review stats), instead of re-creating it. */
  trackByPpaId(_index: number, ppa: PpaModel): number {
    return ppa.id
  }

  setView(view: PpaView): void {
    this.view = view
    savePpaView(view)
  }

  /** e.g. "Abia", "Aba North, Abia" or "all states". */
  /** The dropdowns' text: the API's names are in capitals ("LAGOS"), shown as on /sales ("Lagos"). */
  title(name: string): string {
    return titleCase(name)
  }

  stateName(stateId: number | ''): string {
    const state = this.ng_states.find((s) => s.id === stateId)?.name
    return state ? titleCase(state) : 'All states'
  }

  lgaName(lgaId: number | ''): string {
    const lga = this.ng_states_lgas.find((l) => l.id === lgaId)?.name
    return lga ? titleCase(lga) : 'All LGAs'
  }

  get filterPlaceLabel(): string {
    const stateId = this.filterForm.get('state_id')?.value
    const lgaId = this.filterForm.get('state_lga_id')?.value
    const state = this.ng_states.find((s) => s.id === stateId)?.name
    const lga = this.ng_states_lgas.find((l) => l.id === lgaId)?.name
    if (!state) {
      return 'all states'
    }
    return lga ? `${titleCase(lga)}, ${titleCase(state)}` : titleCase(state)
  }

  get filtersChanged(): boolean {
    const stateId = this.filterForm.get('state_id')?.value || null
    return stateId !== this.defaultStateId || !!this.filterForm.get('state_lga_id')?.value
  }

  get totalPages(): number {
    return Math.ceil(this.totalPpas / this.pageSize) || 1
  }

  isFetching: boolean = true
  fetchError: boolean = false
  private latestRequest = 0

  /** "Show more" (a phone) is loading the next page, or that failed - the button retries. */
  isFetchingMore = false
  moreError = false

  /** More PPAs than are shown - "Show more" adds the next page. */
  get hasMore(): boolean {
    return this.displayPpaData.length < this.totalPpas
  }

  ngOnInit() {
    // Synchronous on subscribe, so the first load already knows. Turning the phone (or resizing) past
    // the breakpoint with more than a page shown starts again at the first page: page numbers and
    // "Show more" count pages differently.
    this.breakpointObserver.observe(PPA_PHONE_QUERY).pipe(takeUntil(this.destroy$)).subscribe(({ matches }) => {
      const changed = this.isPhone !== matches
      this.isPhone = matches
      if (changed && this.currentPage > 1) {
        this.currentPage = 1
        this.loadPpas()
      }
    })

    // The list lives in the store, so a card's new review stats (or a newly added PPA) show here.
    this.store.select(selectFeaturePpas).pipe(takeUntil(this.destroy$)).subscribe((ppas) => {
      this.displayPpaData = ppas.list
      this.totalPpas = ppas.total
    })

    // Load states
    this.callerService.fetchAllNigeriaStates().subscribe({
      next: (res: HttpResponse<any>) => {
        if (res.status === HttpStatusCode.Ok) {
          this.ng_states = res.body?.states || []
        }
        this.statesLoaded$.next()
      },
      error: (err) => {
        this.callerService.showNotification('Failed to load States data')
        this.statesLoaded$.next() // still load PPAs, just without the default state
      },
    })

    // Listen to state changes to load LGAs
    this.filterForm.get('state_id')?.valueChanges
      .pipe(filter((_v) => !!_v), takeUntil(this.destroy$))
      .subscribe({
        next: (stateId) => {
          this.filterForm.get('state_lga_id')?.setValue('') // Reset LGA when state changes
          this.callerService.fetchNigeriaStateLGAs(stateId).subscribe({
            next: (res: HttpResponse<any>) => {
              if (res.status === HttpStatusCode.Ok) {
                this.ng_states_lgas = res.body?.lgas || []
              }
            },
            error: (err) => {
              this.callerService.showNotification("Failed to load State's LGAs")
            },
          })
        },
      })

    // Back to page 1 and reload when the filters change. The debounce folds the state change and
    // the LGA reset it triggers into one request. Subscribed before the default state is applied
    // below, which can happen synchronously (cached states).
    this.filterForm.valueChanges.pipe(debounceTime(50), takeUntil(this.destroy$)).subscribe(() => {
      this.currentPage = 1
      this.loadPpas()
    })

    // Open on the corp member's own state, once both the states list and their profile are in.
    // The first load waits for this, so the page doesn't flash all states first.
    // TODO: open on their LGA too (corper.lga_id), like the state. Mind that changing state_id resets
    // state_lga_id (the valueChanges above), so set the LGA once its list has loaded; keep it one
    // request (the debounce); make defaultLgaId part of filtersChanged, "Clear" and "Back to my
    // state"; and fall back to the whole state when their LGA has no PPAs yet.
    combineLatest([this.statesLoaded$, this.store.select(selectFeatureCorpMember)])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([, corper]: [void, CorpMemberState]) => {
        const state = this.ng_states.find(
          (s) => !!corper?.state_short_name && s.short_name?.toLowerCase() === corper.state_short_name.toLowerCase(),
        )
        if (state && !this.filterForm.get('state_id')?.value) {
          this.defaultStateId = state.id
          this.filterForm.get('state_id')?.setValue(state.id) // valueChanges loads the page
        } else {
          this.loadPpas()
        }
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /** `more`: the next page, added below the ones shown ("Show more") - else the current page, in their place. */
  loadPpas(more = false): void {
    if (more) {
      this.isFetchingMore = true
      this.moreError = false
    } else {
      this.isFetching = true
      this.fetchError = false
      this.isFetchingMore = false
      this.moreError = false
    }
    const requestId = ++this.latestRequest
    this.callerService
      .getAllPPAs({
        state_id: this.filterForm.get('state_id')?.value || null,
        lga_id: this.filterForm.get('state_lga_id')?.value || null,
        sort: this.sortBy,
        limit: this.pageSize,
        offset: (this.currentPage - 1) * this.pageSize,
      })
      .subscribe({
      next: (data: HttpResponse<any>) => {
        if (requestId !== this.latestRequest) {
          return // a newer filter/sort/page request is in flight
        }
        this.isFetching = false
        this.isFetchingMore = false
        if (data.status === HttpStatusCode.Ok) {
          const ppas = data.body.ppas ?? []
          const total = data.body.total ?? ppas.length
          this.store.dispatch(more ? morePpasLoaded({ ppas, total }) : ppasLoaded({ ppas, total }))
        }
      },
      error: () => {
        if (requestId !== this.latestRequest) {
          return
        }
        if (more) {
          this.isFetchingMore = false
          this.moreError = true
          this.currentPage-- // that page isn't shown - the retry asks for it again
          return
        }
        this.isFetching = false
        this.fetchError = true
      },
    })
  }


  // Pagination methods
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page
      this.loadPpas()
    }
  }

  /** A phone's "Show more": the next page, below the ones shown. */
  showMore(): void {
    if (this.isFetchingMore || this.isFetching || !this.hasMore) {
      return
    }
    this.currentPage++
    this.loadPpas(true)
  }

  previousPage(): void {
    this.goToPage(this.currentPage - 1)
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1)
  }

  /** Back to the default: the corp member's own state (or all states if we don't know it). */
  clearFilters(): void {
    this.filterForm.setValue({ state_id: this.defaultStateId ?? '', state_lga_id: '' }) // valueChanges reloads
  }

  showAllStates(): void {
    this.filterForm.setValue({ state_id: '', state_lga_id: '' })
  }

  openMorePpaDetails(ppa: PpaModel | any) {
    try {
      const dialogRef = this.dialog.open(MoreViewSearchComponent, {
        maxWidth: '100dvw',
        maxHeight: '100dvh',
        height: '100%',
        width: '100%',
        data: {
          ppa,
        },
        ariaLabel: 'Dialog to show more details about a PPA',
        role: 'dialog',
      })

      /* record that this house was seen, probably record how long it was seen. tell the agent who posted it? */
      dialogRef.afterClosed().subscribe((result) => {
        console.log('The view more dialog was closed', result)
      })
    } catch (error) {
      console.log('err opening dialog to view more ppa details', error)
    }
  }

}
